import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useScheduleStore } from "@/stores/useScheduleStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "../../../../../../roles/types/enums";
import {
  GeneralDialogProps,
  ROUTES,
  TABLE_NAMES,
  TableRowData,
  TUTORIAL_IDS,
  useMediaQueries
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import ConfirmCloseDialog from "../../../../../Schedules/components/ConfirmCloseDialog/ConfirmCloseDialog";
import { ResourceParams } from "../../../../../Schedules/types";
import useTutorialMode from "../../../../../TutorialMode/hooks/useTutorialMode";
import useSchedulesMainDialog from "../../../hooks/useSchedulesMainDialog";
import SchedulesMainDialogContent from "./SchedulesMainDialogContent/SchedulesMainDialogContent";

interface SchedulesMainDialogProps {
  route: ROUTES;
  resourceParams: ResourceParams;
}

const SchedulesMainDialog = ({
  route,
  isOpen,
  resourceParams,
  onClose
}: GeneralDialogProps & SchedulesMainDialogProps) => {
  const { getAccessStatus } = useAuth();
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { handleCommonError, handleResponse } = useResponse();
  const { putData } = useAxiosPrivate();
  const { isTutorialOpen } = useTutorialMode();

  const {
    selectedRows,
    data: pageData,
    params: { filters: filterParams }
  } = useTableStoreByRoute(route);

  const { selectedSchedule, setSelectedSchedule } = useScheduleStore();

  const [isLoading, setIsLoading] = useState(false);
  const [triSourceTabValue, setTriSourceTabValue] = useState(0);
  const [disableFilteredTab, setDisableFilteredTab] = useState<boolean>(false);
  const [disableSelectedTab, setDisableSelectedTab] = useState<boolean>(false);
  const [filteredItems, setFilteredItems] = useState<string[]>([]);

  const resourceCount = selectedRows?.length;

  const isReady =
    (selectedSchedule && resourceCount > 0) ||
    (selectedSchedule && triSourceTabValue === 2) ||
    (selectedSchedule && triSourceTabValue === 1 && resourceCount === 0);

  const { actionMain, resourceName } = resourceParams;

  const {
    handleCloseSchedulesMainDialog,
    isCloseDialogConfirmationOpen,
    handleConfirmCloseDialog,
    handleSuccess
  } = useSchedulesMainDialog({
    route,
    resourceCount,
    isTutorialOpen,
    onClose,
    actionMain,
    resourceParams,
    handleResponse
  });

  useEffect(() => {
    const filteredItemsIds = pageData?.map(
      (rowItem: TableRowData) => rowItem.id
    );

    setFilteredItems(filteredItemsIds as string[]);
  }, [pageData]);

  useEffect(() => {
    const filteredValues = filterParams;

    const filtersFlatMap =
      filteredValues &&
      Object.values(filteredValues).flatMap((value: any) => [...value]);

    const showSelected =
      (selectedRows?.length > 0 && !filtersFlatMap) ||
      (selectedRows?.length > 0 && filtersFlatMap.length === 0) ||
      (selectedRows?.length > 0 && filtersFlatMap.length > 0);

    const showFiltered =
      selectedRows?.length === 0 && filtersFlatMap?.length > 0;

    let initialTriSourceTabValue: number;

    if (showSelected) {
      initialTriSourceTabValue = 0;
    } else if (showFiltered) {
      initialTriSourceTabValue = 1;
    } else {
      initialTriSourceTabValue = 2;
    }

    setDisableFilteredTab((!showSelected && !showFiltered) || showSelected);
    setDisableSelectedTab((!showSelected && !showFiltered) || showFiltered);

    setTriSourceTabValue(initialTriSourceTabValue);
  }, [selectedRows, filterParams, route]);

  const handleSubmit = async () => {
    if (isReady) {
      const isFilteredTabActive =
        triSourceTabValue === 1 && resourceCount === 0;

      const applyToAll = triSourceTabValue === 2;

      try {
        setIsLoading(true);
        const isVehicleSchedules = resourceName === TABLE_NAMES.Vehicles;

        const itemsArray = isFilteredTabActive ? filteredItems : selectedRows;

        const payload = {
          scheduleId: selectedSchedule.id,
          ...(isVehicleSchedules
            ? { vehicleIds: itemsArray }
            : { driverIds: itemsArray })
        };

        const applyToAllPayload = {
          scheduleId: selectedSchedule.id
        };

        if (applyToAll) {
          await putData(
            resourceParams.apiResourceApplyToAll,
            applyToAllPayload
          );
        } else {
          await putData(resourceParams.apiResourceApply, payload);
        }

        handleSuccess(
          selectedRows,
          isVehicleSchedules,
          triSourceTabValue === 2,
          isFilteredTabActive ? filteredItems : undefined, // filteredItems are the items that are shown in the table after filtering
          triSourceTabValue === 2 ? filteredItems : undefined // allItems here are all the items that are shown in the table - same values as filteredItems if no filter is applied
        );
      } catch (error) {
        handleCommonError(error, t);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const deselectSchedule = () => {
    setSelectedSchedule(null);
  };

  const canManageSchedules = getAccessStatus(USER_PERMISSIONS.ManageSchedules);

  const descriptionType = canManageSchedules
    ? "description"
    : "description read-only";

  return (
    <>
      <Dialog
        title={t(`Schedules##${actionMain}##title`)}
        description={t(`Schedules##${actionMain}##${descriptionType}`)}
        extendToMaxWidth={toMd}
        isTitleSeparator
        isActionsSeparator
        disableEscapeKeyDown={isTutorialOpen}
        open={isOpen}
        close={handleCloseSchedulesMainDialog}
        submit={handleSubmit}
        width={toMd ? undefined : DialogWidth.Dialog920px}
        slotProps={{
          transition: { onExited: deselectSchedule },
          paper: { id: TUTORIAL_IDS.SchedulesMainDialog }
        }}
        actions={
          <DialogActions
            onClose={handleCloseSchedulesMainDialog}
            isLoading={isLoading}
            isreadyToConfirm={isReady}
            hideAcceptButton={!canManageSchedules}
            confirmationButtonText={t("Button##apply")}
            confirmationButtonTestId={
              TUTORIAL_IDS.SchedulesMainDialogApplyButton
            }
            cancelButtonText={t("Button##close")}
            cancelButtonTestId={TUTORIAL_IDS.SchedulesMainDialogCloseButton}
            hasTooltip={!isReady}
            tooltipText={t("Schedules##apply button tooltip", {
              resourceName: resourceName?.slice(0, resourceName?.length - 1)
            })}
          />
        }
      >
        <SchedulesMainDialogContent
          route={route}
          resourceParams={resourceParams}
          resourceCount={resourceCount}
          canManageSchedules={canManageSchedules}
          deselectSchedule={deselectSchedule}
          tabValue={triSourceTabValue}
          setTabValue={setTriSourceTabValue}
          disableFilteredTab={disableFilteredTab}
          disableSelectedTab={disableSelectedTab}
        />
      </Dialog>
      <ConfirmCloseDialog
        resourceName={resourceName}
        isOpen={isCloseDialogConfirmationOpen}
        isLoading={isLoading}
        selectedSchedule={selectedSchedule}
        closeWithMainDialog={handleConfirmCloseDialog}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export default SchedulesMainDialog;
